import React from "react"

import type { CompareByDimension } from "@/metrics/compare-by"
import { Tooltip } from "@coval/ui"

import type { RunMetricOutput } from "@/types/RunMetricOutput"
import { formatNumber } from "@/utils/formatting/format-number"

export type GroupedAggregationMode = "avg" | "median" | "p95" | "min" | "max"

export interface GroupedSimulationCardOptions {
  dimension: CompareByDimension
  metadataKey?: string
  groupKey: string
  metricOutputs: RunMetricOutput[]
  mode?: GroupedAggregationMode
}

interface GroupedAggregationStrategies {
  getNumericValues: (metricOutputs: RunMetricOutput[]) => number[]
  getAverage: (values: number[]) => number | null
  getMedian: (values: number[]) => number | null
  getP95: (values: number[]) => number | null
  getMin: (values: number[]) => number | null
  getMax: (values: number[]) => number | null
}

interface GroupedSimulationCardContext {
  aggregateUnits: string
  unit: string
  strategies: GroupedAggregationStrategies
}

export function getNumericValuesForAggregation(
  metricOutputs: RunMetricOutput[],
): number[] {
  return metricOutputs
    .map((metricOutput) =>
      metricOutput.output_type === "float" &&
      typeof metricOutput.float_value === "number"
        ? metricOutput.float_value
        : null,
    )
    .filter(
      (value): value is number => value !== null && Number.isFinite(value),
    )
}

export function getAverageValue(values: number[]): number | null {
  if (!values.length) {
    return null
  }
  return values.reduce((sum, value) => sum + value, 0) / values.length
}

export function getMedianValue(values: number[]): number | null {
  if (!values.length) {
    return null
  }
  const sorted = [...values].sort((left, right) => left - right)
  const middleIndex = Math.floor(sorted.length / 2)
  if (sorted.length % 2 === 0) {
    return (sorted[middleIndex - 1] + sorted[middleIndex]) / 2
  }
  return sorted[middleIndex]
}

export function getP95Value(values: number[]): number | null {
  if (!values.length) {
    return null
  }
  const sorted = [...values].sort((left, right) => left - right)
  const index = 0.95 * (sorted.length - 1)
  const lowIndex = Math.floor(index)
  const highIndex = Math.ceil(index)
  if (lowIndex === highIndex) {
    return sorted[lowIndex]
  }
  return (
    sorted[lowIndex] +
    (index - lowIndex) * (sorted[highIndex] - sorted[lowIndex])
  )
}

export function getMinValue(values: number[]): number | null {
  return values.length ? Math.min(...values) : null
}

export function getMaxValue(values: number[]): number | null {
  return values.length ? Math.max(...values) : null
}

function aggregateValues(
  values: number[],
  mode: GroupedAggregationMode,
  strategies: GroupedAggregationStrategies,
): number | null {
  if (mode === "p95") {
    return strategies.getP95(values)
  }
  if (mode === "min") {
    return strategies.getMin(values)
  }
  if (mode === "max") {
    return strategies.getMax(values)
  }
  if (mode === "median") {
    return strategies.getMedian(values)
  }
  return strategies.getAverage(values)
}

function getGroupLabel(options: GroupedSimulationCardOptions): string {
  if (options.dimension === "metadata" && options.metadataKey) {
    return `${options.metadataKey} = ${options.groupKey}`
  }
  if (options.dimension === "agent") {
    return `Agent ${options.groupKey}`
  }
  if (options.dimension === "persona") {
    return `Persona ${options.groupKey}`
  }
  if (options.dimension === "test_case") {
    return `Test case ${options.groupKey}`
  }
  return options.groupKey
}

function getModeLabel(mode: GroupedAggregationMode): string {
  if (mode === "p95") {
    return "P95"
  }
  if (mode === "median") {
    return "Median"
  }
  if (mode === "min") {
    return "Min"
  }
  if (mode === "max") {
    return "Max"
  }
  return "Average"
}

export function renderGroupedSimulationCard(
  options: GroupedSimulationCardOptions,
  context: GroupedSimulationCardContext,
): React.ReactNode {
  if (!options.metricOutputs.length) {
    return <div className="text-coval-text-muted text-xs">N/A</div>
  }

  const numericValues = context.strategies.getNumericValues(
    options.metricOutputs,
  )
  if (!numericValues.length) {
    return <div className="text-coval-text-muted text-xs">—</div>
  }

  const mode = options.mode ?? "avg"
  const aggregate = aggregateValues(numericValues, mode, context.strategies)
  const displayValue =
    aggregate !== null && Number.isFinite(aggregate)
      ? formatNumber(aggregate)
      : `${numericValues.length}`
  const unitSuffix = context.aggregateUnits || context.unit
  const chipText = `${displayValue}${unitSuffix ? ` ${unitSuffix}` : ""}`
  const tooltip = `${getGroupLabel(options)} — ${getModeLabel(mode)}: ${chipText}`

  return (
    <Tooltip content={tooltip}>
      <span className="inline-flex items-center rounded-sm min-h-[24px] px-2 max-w-[120px] bg-surface-1 truncate text-xs">
        {chipText}
      </span>
    </Tooltip>
  )
}
